import React, { memo, useState } from 'react'
import { rentalApplications } from '@homevest/utils'
import { startCase } from 'lodash'
import {
  Button,
  Heading,
  Select,
  Stack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter
} from '@chakra-ui/react'

import { SpanText } from 'components/Toolkit'
import { BaseFormField } from 'components/ChakraToolkit/FormField'

const { RENTAL_APPLICATION_CANCELATION_REASONS, RENTAL_APPLICATION_CANCELATION_SUBREASONS } =
  rentalApplications

type CancelationReason =
  (typeof RENTAL_APPLICATION_CANCELATION_REASONS)[keyof typeof RENTAL_APPLICATION_CANCELATION_REASONS]

type CancelationSubreason = {
  [k in keyof typeof RENTAL_APPLICATION_CANCELATION_SUBREASONS]: (typeof RENTAL_APPLICATION_CANCELATION_SUBREASONS)[k][keyof (typeof RENTAL_APPLICATION_CANCELATION_SUBREASONS)[k]]
}

interface CancelApplicationProps {
  isOpen: boolean
  close: () => void
  cancel: (cancelReason: string, cancelSubreason: string) => Promise<void>
  isCanceling: boolean
  buildiumLeaseId: number | null
}

const CancelApplication: React.FC<CancelApplicationProps> = ({
  isOpen,
  close,
  cancel,
  isCanceling,
  buildiumLeaseId
}) => {
  const [cancelReason, setCancelReason] = useState<CancelationReason | ''>('')
  const [cancelSubreason, setCancelSubreason] = useState<
    CancelationSubreason[keyof typeof RENTAL_APPLICATION_CANCELATION_SUBREASONS] | ''
  >('')

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <ModalContent>
        <ModalHeader>
          <Heading size='h4'>Why do you want to cancel?</Heading>
        </ModalHeader>
        <ModalBody>
          <Stack direction='column' spacing={4}>
            <BaseFormField<{ reason: CancelationReason }>
              field='reason'
              label='Reason'
              errors={{ reason: '' }}
              isRequired
            >
              <Select
                value={cancelReason || ''}
                onChange={(e) => setCancelReason(e.target.value as CancelationReason)}
              >
                <option value=''>Select a reason</option>
                {Object.values(RENTAL_APPLICATION_CANCELATION_REASONS).map((reason) => (
                  <option key={reason} value={reason}>
                    {startCase(reason)}
                  </option>
                ))}
              </Select>
            </BaseFormField>

            {cancelReason && (
              <BaseFormField<{
                subreason: CancelationSubreason[typeof cancelReason]
              }>
                field='subreason'
                label='Subreason'
                errors={{ subreason: '' }}
                isRequired
              >
                <Select
                  value={cancelSubreason || ''}
                  onChange={(e) => setCancelSubreason(e.target.value)}
                >
                  <option value=''>Select a reason</option>
                  {Object.values(RENTAL_APPLICATION_CANCELATION_SUBREASONS[cancelReason]).map(
                    (subreason) => (
                      <option key={subreason} value={subreason}>
                        {startCase(subreason)}
                      </option>
                    )
                  )}
                </Select>
              </BaseFormField>
            )}
          </Stack>

          {buildiumLeaseId && (
            <SpanText>
              After canceling this application, you will need to either terminate or delete the{' '}
              <a
                href={`https://upandup.managebuilding.com/manager/app/rentroll/${buildiumLeaseId}/summary`}
                target='_blank'
                rel='noopener noreferrer'
                className='font-semibold text-blue-500'
              >
                associated Buildium lease
              </a>
              .
            </SpanText>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme='teal'
            size='xs'
            onClick={async () => {
              if (cancelReason && cancelSubreason) {
                await cancel(cancelReason, cancelSubreason)
                close()
              }
            }}
            isDisabled={!cancelReason || !cancelSubreason || isCanceling}
            isLoading={isCanceling}
          >
            Cancel Application
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default memo(CancelApplication)
